import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

interface GlassCardProps {
  children: ReactNode;
  className?: string;
  hoverEffect?: boolean;
  onClick?: () => void;
  variant?: 'default' | 'subtle' | 'elevated';
}

const GlassCard = ({
  children,
  className,
  hoverEffect = true,
  onClick,
  variant = 'default',
}: GlassCardProps) => {
  const variants = {
    default: {
      // Light: Classic glass | Dark: High contrast for readability
      background: 'bg-white/80 dark:bg-slate-800/95',
      border: 'border-white/30 dark:border-slate-600/60',
      shadow: 'shadow-lg shadow-black/5 dark:shadow-2xl dark:shadow-black/40',
      backdrop: 'backdrop-blur-xl',
      textContrast: 'dark:bg-slate-900/20', // Extra contrast layer for text
    },
    subtle: {
      background: 'bg-white/60 dark:bg-slate-800/90',
      border: 'border-white/20 dark:border-slate-600/50',
      shadow: 'shadow-md shadow-black/5 dark:shadow-xl dark:shadow-black/30',
      backdrop: 'backdrop-blur-lg',
      textContrast: 'dark:bg-slate-900/15',
    },
    elevated: {
      background: 'bg-white/85 dark:bg-slate-700/95',
      border: 'border-white/40 dark:border-slate-500/70',
      shadow: 'shadow-xl shadow-black/10 dark:shadow-2xl dark:shadow-black/50',
      backdrop: 'backdrop-blur-2xl',
      textContrast: 'dark:bg-slate-900/25',
    },
  };

  const variantStyles = variants[variant];

  return (
    <div className="relative w-full">
      {/* Card container */}
      <div
        className={cn(
          'relative group rounded-2xl transition-all duration-300 ease-out w-full h-full',
          hoverEffect && [
            'hover:shadow-lg hover:shadow-black/10',
            'dark:hover:shadow-2xl dark:hover:shadow-black/40',
          ],
          className
        )}
        onClick={onClick}>
        {/* Primary glass surface */}
        <div
          className={cn(
            'relative rounded-2xl transition-all duration-300',
            variantStyles.background,
            variantStyles.backdrop,
            variantStyles.border,
            variantStyles.shadow,
            'border overflow-hidden'
          )}>
          {/* Light mode: Frosted glass effect */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-br from-white/40 via-white/20 to-white/5 dark:hidden" />

          {/* Light mode: Top highlight */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/30 dark:hidden" />

          {/* Dark mode: Text contrast enhancement layer */}
          <div
            className={cn(
              'absolute inset-[1px] rounded-2xl hidden dark:block',
              variantStyles.textContrast
            )}
          />

          {/* Dark mode: Subtle top highlight for depth */}
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-slate-300/10 hidden dark:block" />

          {/* Brand gradient overlay - very subtle */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-eyevantage-primary/2 via-transparent via-40% to-eyevantage-secondary/2 opacity-80" />

          {/* Content container */}
          <div className="relative w-full h-full z-20 p-3">{children}</div>
        </div>
      </div>
    </div>
  );
};

export default GlassCard;
